/**
 * LinkMatchesProcessor - Utility class for calculating matches between existing items
 * (processes/issues) and task subtasks based on viewable locations.
 */

/**
 * Build ancestor chain from task to root (closest first)
 * @param {Object} task - The current task
 * @param {Array} ancestorChain - Existing ancestor chain
 * @returns {Array} - Chain with task prepended
 */
export function getAncestorChain(task, ancestorChain = []) {
  return [task, ...ancestorChain];
}

/**
 * Find the closest ancestor with a matching locationId
 * @param {Array} ancestorChain - Chain of ancestors (closest first)
 * @param {string|number} locationId - Location ID to match
 * @returns {Object|null} - Matching ancestor or null
 */
export function findClosestMatchingAncestor(ancestorChain, locationId) {
  for (const ancestor of ancestorChain) {
    if (ancestor.viewableLocationIds?.includes(locationId)) {
      return ancestor;
    }
  }
  return null;
}

/**
 * Collect all linked item IDs from subtasks across the entire task tree
 * @param {Array} tasks - Array of task objects
 * @returns {Object} - Contains linkedIssueIds, linkedProcessIds, linkedBySubtask
 */
export function collectLinkedItemIds(tasks) {
  const linkedIssueIds = new Set();
  const linkedProcessIds = new Set();
  // Map of "taskId:itemType:templateCode" -> Set of linked item IDs for that specific subtask
  const linkedBySubtask = new Map();

  const traverse = (task) => {
    if (task.subtasks && task.subtasks.length > 0) {
      for (const subtask of task.subtasks) {
        if (subtask.itemId) {
          // Add to global sets
          if (subtask.itemType === 'issue') {
            linkedIssueIds.add(subtask.itemId);
          } else if (subtask.itemType === 'process') {
            linkedProcessIds.add(subtask.itemId);
          }
          // Add to per-subtask map
          const key = `${task.id}:${subtask.itemType}:${subtask.itemTemplateCode}`;
          if (!linkedBySubtask.has(key)) {
            linkedBySubtask.set(key, new Set());
          }
          linkedBySubtask.get(key).add(subtask.itemId);
        }
      }
    }
    if (task.children && task.children.length > 0) {
      for (const child of task.children) {
        traverse(child);
      }
    }
  };

  for (const task of tasks) {
    traverse(task);
  }

  return { linkedIssueIds, linkedProcessIds, linkedBySubtask };
}

/**
 * Find matching items for a task and its children
 * @param {Object} task - The task to find matches for
 * @param {Object} options - Configuration options
 * @param {Array} options.ancestorChain - Existing ancestor chain
 * @param {Object} options.selectedTemplateCodes - Template codes by type { issue: Set, process: Set }
 * @param {Array} options.issues - All issues to search
 * @param {Array} options.processes - All processes to search
 * @param {Object} options.itemTemplatesByCode - Template lookup by "type:code"
 * @param {boolean} options.includeLinkedItems - Whether to include already-linked items
 * @param {Set} options.linkedIssueIds - Set of all linked issue IDs
 * @param {Set} options.linkedProcessIds - Set of all linked process IDs
 * @param {Map} options.linkedBySubtask - Map of subtask keys to linked item IDs
 * @returns {Array} - Array of match objects
 */
export function findMatchingItems(task, options = {}) {
  const {
    ancestorChain = [],
    selectedTemplateCodes = {},
    issues = [],
    processes = [],
    itemTemplatesByCode = {},
    includeLinkedItems = false,
    linkedIssueIds = new Set(),
    linkedProcessIds = new Set(),
    linkedBySubtask = new Map()
  } = options;

  const matches = [];
  const currentChain = getAncestorChain(task, ancestorChain);

  // Get selected template codes by type
  const issueTemplateCodes = selectedTemplateCodes['issue'] || new Set();
  const processTemplateCodes = selectedTemplateCodes['process'] || new Set();

  // Check if task has subtasks to match
  if (task.subtasks && task.subtasks.length > 0) {
    for (const subtask of task.subtasks) {
      const { itemType, itemTemplateCode } = subtask;

      if (itemType === 'issue') {
        // Skip if no issue templates available or this template code is not in the selection
        if (issueTemplateCodes.size === 0 || !issueTemplateCodes.has(itemTemplateCode)) {
          continue;
        }

        // Get items already linked to THIS specific subtask
        const subtaskKey = `${task.id}:issue:${itemTemplateCode}`;
        const linkedToThisSubtask = linkedBySubtask.get(subtaskKey) || new Set();

        // Find issues matching the template code
        const templateMatches = issues.filter(issue => {
          const templateMatch = issue.issueTemplateCode === itemTemplateCode;
          // Always include items linked to THIS subtask (so they show as "linked")
          const isLinkedToThisSubtask = linkedToThisSubtask.has(issue.id);
          // If includeLinkedItems is false, exclude items that are linked to OTHER subtasks
          const linkedFilter = includeLinkedItems || isLinkedToThisSubtask || !linkedIssueIds.has(issue.id);
          return templateMatch && linkedFilter;
        });

        // For each matching issue, find the closest ancestor with matching locationId
        for (const issue of templateMatches) {
          const closestAncestor = findClosestMatchingAncestor(currentChain, issue.locationId);
          if (closestAncestor) {
            const template = itemTemplatesByCode[`issue:${issue.issueTemplateCode}`];
            const matchDepth = currentChain.indexOf(closestAncestor);
            // Build hierarchy path to the ancestor where the viewableLocation matched
            // currentChain is [task, parent, grandparent, ...] (closest first)
            // We want the path from root to closestAncestor (inclusive of closestAncestor)
            const ancestorHierarchy = currentChain.slice(matchDepth).map(t => t.title).reverse().join(' > ');
            matches.push({
              taskId: task.id,
              taskTitle: task.title,
              subtaskTitle: subtask.title,
              itemType: 'issue',
              itemId: issue.id,
              itemClientId: issue.clientId,
              itemTitle: template?.title || 'Untitled Issue',
              itemPercentComplete: issue.progress,
              itemLocationTitle: issue.locationTitle,
              drawingId: issue.drawingId,
              templateCode: issue.issueTemplateCode,
              matchedAt: closestAncestor.title,
              matchDepth,
              taskHierarchy: ancestorHierarchy,
              isLinked: linkedToThisSubtask.has(issue.id)
            });
          }
        }
      } else if (itemType === 'process') {
        // Skip if no process templates available or this template code is not in the selection
        if (processTemplateCodes.size === 0 || !processTemplateCodes.has(itemTemplateCode)) {
          continue;
        }

        // Get items already linked to THIS specific subtask
        const subtaskKey = `${task.id}:process:${itemTemplateCode}`;
        const linkedToThisSubtask = linkedBySubtask.get(subtaskKey) || new Set();

        // Find processes matching the template code
        const templateMatches = processes.filter(process => {
          const templateMatch = process.processTemplateCode === itemTemplateCode;
          const isLinkedToThisSubtask = linkedToThisSubtask.has(process.id);
          const linkedFilter = includeLinkedItems || isLinkedToThisSubtask || !linkedProcessIds.has(process.id);
          return templateMatch && linkedFilter;
        });

        // For each matching process, find the closest ancestor with matching locationId
        for (const process of templateMatches) {
          const closestAncestor = findClosestMatchingAncestor(currentChain, process.locationId);
          if (closestAncestor) {
            const template = itemTemplatesByCode[`process:${process.processTemplateCode}`];
            const matchDepth = currentChain.indexOf(closestAncestor);
            // Build hierarchy path to the ancestor where the viewableLocation matched
            // currentChain is [task, parent, grandparent, ...] (closest first)
            // We want the path from root to closestAncestor (inclusive of closestAncestor)
            const ancestorHierarchy = currentChain.slice(matchDepth).map(t => t.title).reverse().join(' > ');
            matches.push({
              taskId: task.id,
              taskTitle: task.title,
              subtaskTitle: subtask.title,
              itemType: 'process',
              itemId: process.id,
              itemClientId: process.clientId,
              itemTitle: template?.title || 'Untitled Process',
              itemPercentComplete: process.progress,
              itemLocationTitle: process.locationTitle,
              drawingId: process.drawingId,
              templateCode: process.processTemplateCode,
              matchedAt: closestAncestor.title,
              matchDepth,
              taskHierarchy: ancestorHierarchy,
              isLinked: linkedToThisSubtask.has(process.id)
            });
          }
        }
      }
    }
  }

  // Recursively check child tasks, passing the ancestor chain
  if (task.children && task.children.length > 0) {
    for (const child of task.children) {
      matches.push(...findMatchingItems(child, {
        ...options,
        ancestorChain: currentChain
      }));
    }
  }

  return matches;
}

/**
 * Compute auto-assignments for all subtasks based on matches
 * Processes tasks top-to-bottom, assigning the best available match (nearest location) to each subtask
 * Each item can only be assigned once across all subtasks
 * @param {Array} allMatches - All match objects from findMatchingItems
 * @returns {Object} - Contains autoAssignments Map and itemToSubtaskMap Map
 */
export function computeAutoAssignments(allMatches) {
  const autoAssignments = new Map();
  const itemToSubtaskMap = new Map();

  // Track which items are already claimed (either linked or auto-assigned)
  const claimedItems = new Set();

  // First, mark all already-linked items as claimed
  for (const match of allMatches) {
    if (match.isLinked) {
      const itemKey = `${match.itemType}:${match.itemId}`;
      claimedItems.add(itemKey);

      // Also record the auto-assignment for linked items
      const subtaskKey = `${match.taskId}:${match.subtaskTitle}|${match.itemType}`;
      autoAssignments.set(subtaskKey, {
        itemId: match.itemId,
        itemType: match.itemType,
        itemTitle: match.itemTitle,
        itemClientId: match.itemClientId,
        isLinked: true,
        isAutoAssigned: false
      });
      itemToSubtaskMap.set(itemKey, subtaskKey);
    }
  }

  // Group matches by subtask, preserving task order (which is top-to-bottom from tree traversal)
  const subtaskOrder = [];
  const subtaskMatchesMap = {};

  for (const match of allMatches) {
    const subtaskKey = `${match.taskId}:${match.subtaskTitle}|${match.itemType}`;

    if (!subtaskMatchesMap[subtaskKey]) {
      subtaskMatchesMap[subtaskKey] = [];
      subtaskOrder.push(subtaskKey);
    }
    subtaskMatchesMap[subtaskKey].push(match);
  }

  // Process subtasks in order (top-to-bottom as encountered in tree)
  for (const subtaskKey of subtaskOrder) {
    // Skip if already has a linked item
    if (autoAssignments.has(subtaskKey)) {
      continue;
    }

    const matches = subtaskMatchesMap[subtaskKey];

    // Filter to only unclaimed items
    const availableMatches = matches.filter(m => {
      const itemKey = `${m.itemType}:${m.itemId}`;
      return !claimedItems.has(itemKey);
    });

    if (availableMatches.length === 0) {
      continue;
    }

    // Sort by matchDepth (ascending) - lower depth = closer match
    availableMatches.sort((a, b) => a.matchDepth - b.matchDepth);

    // Auto-assign the best (closest) match
    const bestMatch = availableMatches[0];
    const itemKey = `${bestMatch.itemType}:${bestMatch.itemId}`;

    // Claim this item
    claimedItems.add(itemKey);

    // Record the auto-assignment
    autoAssignments.set(subtaskKey, {
      itemId: bestMatch.itemId,
      itemType: bestMatch.itemType,
      itemTitle: bestMatch.itemTitle,
      itemClientId: bestMatch.itemClientId,
      matchDepth: bestMatch.matchDepth,
      isLinked: false,
      isAutoAssigned: true
    });
    itemToSubtaskMap.set(itemKey, subtaskKey);
  }

  return { autoAssignments, itemToSubtaskMap };
}

/**
 * Get counts per task for linked, multiple, matched (single), and unmatched subtasks
 * @param {string|number} taskId - The task ID
 * @param {Array} matches - Matches for this task
 * @param {Map} pendingChanges - User's pending selection changes
 * @param {Map} autoAssignments - Auto-assigned selections
 * @returns {Object} - Contains linked, multiple, matched counts
 */
export function getTaskMatchCounts(taskId, matches, pendingChanges, autoAssignments) {
  // Group matches by subtask (subtaskTitle + itemType)
  const subtaskGroups = {};
  for (const match of matches) {
    const key = `${match.subtaskTitle}|${match.itemType}`;
    if (!subtaskGroups[key]) {
      subtaskGroups[key] = [];
    }
    subtaskGroups[key].push(match);
  }

  let linked = 0;
  let multiple = 0;
  let matched = 0;

  for (const [subtaskKey, items] of Object.entries(subtaskGroups)) {
    const linkedItem = items.find(item => item.isLinked);
    const hasLinked = !!linkedItem;

    // Check if this subtask has an assignment (auto or user)
    const fullSubtaskKey = `${taskId}:${subtaskKey}`;
    const userChange = pendingChanges.get(fullSubtaskKey);

    // Check if user explicitly deselected (newItemId is null)
    const hasExplicitDeselection = userChange && userChange.newItemId === null;

    // If there's a linked item but user selected a DIFFERENT item, count as matched (blue) not linked (green)
    if (hasLinked && userChange && userChange.newItemId !== null && userChange.newItemId !== linkedItem.itemId) {
      matched++;
    } else if (hasLinked && hasExplicitDeselection) {
      // User explicitly deselected a linked item - count as multiple (orange) to indicate needs attention
      multiple++;
    } else if (hasLinked) {
      // Linked items with no change or same selection are counted as linked (green)
      linked++;
    } else if (hasExplicitDeselection) {
      // User explicitly deselected - count as multiple (orange) to indicate needs attention
      multiple++;
    } else if (userChange && userChange.newItemId !== null) {
      // Has user selection with a valid item - count as matched (blue)
      matched++;
    } else if (autoAssignments.has(fullSubtaskKey)) {
      // Has auto-assignment - count as matched (blue)
      matched++;
    } else if (items.length > 1) {
      // Multiple matches with no assignment - count as multiple (orange)
      multiple++;
    } else if (items.length === 1) {
      // Single match - should have been auto-assigned, but count as matched just in case
      matched++;
    }
  }

  return { linked, multiple, matched };
}

/**
 * Count unmatched subtasks for a task (subtasks that have no matches)
 * @param {Object} task - The task object with subtasks
 * @param {Array} matches - Matches for this task
 * @returns {number} - Count of unmatched subtasks
 */
export function getUnmatchedCount(task, matches) {
  if (!task.subtasks) return 0;

  let unmatched = 0;
  for (const subtask of task.subtasks) {
    // Check if this subtask's template has any matches
    const hasMatch = matches.some(m =>
      m.itemType === subtask.itemType && m.templateCode === subtask.itemTemplateCode
    );
    if (!hasMatch) {
      unmatched++;
    }
  }
  return unmatched;
}

/**
 * Calculate subtask match counts for the progress bar
 * @param {Array} matchedItems - All matched items
 * @param {Map} pendingChanges - User's pending selection changes
 * @param {Map} autoAssignments - Auto-assigned selections
 * @returns {Object} - Contains multiple and matched counts
 */
export function calculateSubtaskMatchCounts(matchedItems, pendingChanges, autoAssignments) {
  // Group matches by subtask (subtaskTitle + itemType)
  const subtaskGroups = {};
  for (const match of matchedItems) {
    const key = `${match.taskId}|${match.subtaskTitle}|${match.itemType}`;
    if (!subtaskGroups[key]) {
      subtaskGroups[key] = [];
    }
    subtaskGroups[key].push(match);
  }

  let multiple = 0;
  let matched = 0;

  for (const [groupKey, items] of Object.entries(subtaskGroups)) {
    const linkedItem = items.find(item => item.isLinked);
    const hasLinked = !!linkedItem;

    // Build the full subtask key for checking assignments
    // groupKey format: taskId|subtaskTitle|itemType
    // fullSubtaskKey format: taskId:subtaskTitle|itemType
    const parts = groupKey.split('|');
    const taskId = parts[0];
    const subtaskKey = parts.slice(1).join('|');
    const fullSubtaskKey = `${taskId}:${subtaskKey}`;

    // Check if this subtask has a user change
    const userChange = pendingChanges.get(fullSubtaskKey);

    // Check if user explicitly deselected (newItemId is null)
    const hasExplicitDeselection = userChange && userChange.newItemId === null;

    // If there's a linked item but user selected a DIFFERENT item, count as matched (blue) not linked (green)
    if (hasLinked && userChange && userChange.newItemId !== null && userChange.newItemId !== linkedItem.itemId) {
      matched++;
    } else if (hasLinked && hasExplicitDeselection) {
      // User explicitly deselected a linked item - count as multiple (orange) to indicate needs attention
      multiple++;
    } else if (hasLinked) {
      // Linked items with no change or same selection - already counted elsewhere
    } else if (hasExplicitDeselection) {
      // User explicitly deselected - count as multiple (orange) to indicate needs attention
      multiple++;
    } else if (userChange && userChange.newItemId !== null) {
      // Has user selection with a valid item - count as matched (blue)
      matched++;
    } else if (autoAssignments.has(fullSubtaskKey)) {
      // Has auto-assignment - count as matched (blue)
      matched++;
    } else if (items.length > 1) {
      // Multiple matches with no assignment - count as multiple (orange)
      multiple++;
    } else if (items.length === 1) {
      // Single match - should have been auto-assigned, but count as matched just in case
      matched++;
    }
  }

  return { multiple, matched };
}

/**
 * Count total linked subtasks across all tasks
 * @param {Array} tasks - Array of task objects
 * @returns {number} - Count of linked subtasks
 */
export function countTotalLinkedSubtasks(tasks) {
  let count = 0;
  const countLinked = (taskList) => {
    for (const task of taskList) {
      if (task.subtasks) {
        for (const subtask of task.subtasks) {
          if (subtask.itemId) {
            count++;
          }
        }
      }
      if (task.children) {
        countLinked(task.children);
      }
    }
  };
  countLinked(tasks);
  return count;
}

/**
 * Count total subtasks across all tasks
 * @param {Array} tasks - Array of task objects
 * @returns {number} - Total count of subtasks
 */
export function countTotalSubtasks(tasks) {
  let count = 0;
  const countSubtasks = (taskList) => {
    for (const task of taskList) {
      if (task.subtasks) {
        count += task.subtasks.length;
      }
      if (task.children) {
        countSubtasks(task.children);
      }
    }
  };
  countSubtasks(tasks);
  return count;
}

/**
 * Find all matches for all tasks in a task tree
 * @param {Array} tasks - Array of root tasks
 * @param {Object} options - Options passed to findMatchingItems
 * @returns {Array} - All matches
 */
export function findAllMatches(tasks, options) {
  const allMatches = [];
  for (const task of tasks) {
    allMatches.push(...findMatchingItems(task, options));
  }
  return allMatches;
}
